/**
 * Axios HTTP client configuration
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/utils/constants';
import { authAPI } from './authAPI';
import toast from 'react-hot-toast';
import { isTokenExpiringSoon, isTokenExpired } from '@/utils/jwt';

// Create axios instance

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Invite acceptance must not send the logged-in user's JWT, or the API may reject or mis-handle the invite. */
const isPublicOrganizationInviteEndpoint = (url: string) =>
  url.includes('/organization-invitations/validate') ||
  url.includes('/organization-invitations/send-otp') ||
  url.includes('/organization-invitations/verify-otp') ||
  url.includes('/organization-invitations/set-password');

// Request interceptor - Add auth token and refresh if needed
axiosClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    
    // Skip token refresh for auth endpoints to avoid infinite loops
    const url = config.url || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/refresh');
    const skipAuth = isPublicOrganizationInviteEndpoint(url);
    
    if (token && config.headers && !skipAuth) {
      // Check if token is expired or expiring soon (within 5 minutes)
      if (!isAuthEndpoint && (isTokenExpired(token) || isTokenExpiringSoon(token, 5))) {
        // Token is expired or expiring soon, try to refresh
        if (refreshToken) {
          try {
            const res = await authAPI.refreshToken(refreshToken);
            localStorage.setItem(TOKEN_KEY, res.access_token);
            localStorage.setItem(REFRESH_TOKEN_KEY, res.refresh_token);
            config.headers.Authorization = `Bearer ${res.access_token}`;
          } catch (refreshError) {
            // If refresh fails, clear tokens and let the response interceptor handle logout
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            // Still add the expired token, the response interceptor will handle 401
            config.headers.Authorization = `Bearer ${token}`;
          }
        } else {
          // No refresh token, just use the expired token and let response interceptor handle it
          config.headers.Authorization = `Bearer ${token}`;
        }
      } else {
        // Token is still valid, use it
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors (avoid redirecting on bootstrapping race)
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (newToken: string) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

axiosClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ detail: string }>) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      const originalRequest = error.config as any;
      const token = localStorage.getItem(TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      const currentPath = window.location.pathname;
      const isBootstrapping = currentPath !== '/login' && currentPath !== '/register' && !token;
      const url = (originalRequest?.url || '');
      const isAuthBootstrapCall = url.includes('/auth/me') || url.includes('/auth/refresh');

      // Public invite endpoints use invite token in body, not session JWT — never refresh or logout here
      if (isPublicOrganizationInviteEndpoint(url)) {
        return Promise.reject(error);
      }

      // Try refresh flow once per request; guard concurrent refreshes
      if (!isBootstrapping && refreshToken && !originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          // Queue until refresh completes
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((newToken: string) => {
              try {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                resolve(axiosClient(originalRequest));
              } catch (err) {
                reject(err);
              }
            });
          });
        }

        isRefreshing = true;
        try {
          const res = await authAPI.refreshToken(refreshToken);
          localStorage.setItem(TOKEN_KEY, res.access_token);
          localStorage.setItem(REFRESH_TOKEN_KEY, res.refresh_token);
          isRefreshing = false;
          onRefreshed(res.access_token);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${res.access_token}`;
          return axiosClient(originalRequest);
        } catch (e) {
          isRefreshing = false;
          refreshSubscribers = [];
          // fallthrough to logout below
        }
      }

      // If no refresh, or refresh failed
      // Skip logout for change-password endpoint - it returns 401 for wrong current password
      const isChangePasswordEndpoint = url.includes('/users/me/change-password');
      
      if (!isBootstrapping && currentPath !== '/login' && !isAuthBootstrapCall && !isChangePasswordEndpoint) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        window.location.href = '/login';
        toast.error('Session expired. Please login again.');
      }
    }

    // Handle validation errors
    if (error.response?.status === 422) {
      const detail = error.response.data?.detail;
      toast.error(detail || 'Validation error');
    }

    // Handle not found errors
    if (error.response?.status === 404) {
      toast.error('Resource not found');
    }

    // Handle 413 (Content Too Large) - usually from nginx/reverse proxy
    if (error.response?.status === 413) {
      // Don't show toast here as it will be handled by the specific action
      // But we can log it for debugging
      console.error('File upload rejected: Content too large (413)');
    }

    // Handle server errors
    if (error.response?.status && error.response.status >= 500) {
      toast.error('Server error. Please try again later.');
    }

    return Promise.reject(error);
  }
);

export default axiosClient;


